import { darken, rgba } from 'polished';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { useWorkspace } from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { colors, weights } from '~/styles';
import { useAssistant } from '../../ai/assistant/AssistantContext';
import EnableAssistantModal from '../EnableAssistantModal';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.1875rem;
  background-color: ${rgba(colors.white, 0.1)};
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  width: 9.0625rem;
`;

const CardText = styled.p`
  margin: 0 0 0.75rem;
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  line-height: 1.4;
`;

const AskButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6.5625rem;
  height: 1.5625rem;
  padding: 0;
  border: none;
  border-radius: 2.5rem;
  background-color: ${colors.primary};
  color: ${colors.white};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  cursor: pointer;

  &:hover {
    background-color: ${darken(0.05, colors.primary)};
  }
`;

const HoverLabel = styled.div`
  position: absolute;
  top: 50%;
  left: calc(100% - 0.125rem);
  padding: 0.5rem;
  background-color: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: 0 0 0.625rem ${rgba(colors.black, 0.25)};
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 0.875rem;
  width: max-content;
  max-width: 14rem;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0;
  z-index: 95;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -0.375rem;
    width: 0.375rem;
    height: 0.75rem;
    transform: translateY(-50%);
    background-color: ${colors.white};
    clip-path: polygon(100% 0, 100% 100%, 0 50%);
  }
`;

const BadgeWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;

  &:hover ${HoverLabel} {
    opacity: 1;
  }
`;

const Badge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  background-color: ${rgba(colors.white, 0.1)};
  color: ${colors.white};
  font-size: 1.25rem;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
    color: ${colors.white};
  }
`;

export default function SidebarAiCta({ collapsed }) {
  const auth = useAuth();
  const features = useFeatures();
  const { workspace } = useWorkspace();
  const { togglePanel, openPanel } = useAssistant();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [openAfterEnable, setOpenAfterEnable] = useState(false);

  const isBillingLocked = !workspace?.billingStatus?.isValid;
  const isDisabled = workspace?.statusId === 'disabled';
  const isAdmin = auth.workspace.manage;

  // Open panel after feature is enabled and the context has re-rendered
  useEffect(() => {
    if (openAfterEnable && features.assistant) {
      setOpenAfterEnable(false);
      openPanel();
    }
  }, [openAfterEnable, features.assistant, openPanel]);

  const handleClick = useCallback(() => {
    if (features.assistant) {
      togglePanel();
    } else {
      setShowEnableModal(true);
    }
  }, [features.assistant, togglePanel]);

  const handleEnabled = useCallback(() => {
    setShowEnableModal(false);
    setOpenAfterEnable(true);
  }, []);

  if (isBillingLocked || isDisabled) return null;
  if (features.assistant && !workspace.member.assistantEnabled) return null;
  if (!features.assistant && !isAdmin) return null;

  return (
    <>
      {collapsed ? (
        <BadgeWrapper>
          <Badge onClick={handleClick}>
            <Icon icon="sparkles" />
          </Badge>
          <HoverLabel>Get more done using Ruddr Intelligence.</HoverLabel>
        </BadgeWrapper>
      ) : (
        <Card>
          <CardText>Get more done using Ruddr Intelligence.</CardText>
          <AskButton onClick={handleClick}>Ask Ruddr</AskButton>
        </Card>
      )}
      {showEnableModal && <EnableAssistantModal onClose={() => setShowEnableModal(false)} onEnabled={handleEnabled} />}
    </>
  );
}
